/**
 * Toast Configuration and Theme Tokens for Autobet
 * Centralized settings for position, autoClose timing, animations, and styles.
 */
import { colors } from "../../../../theme";

export const TOAST_POSITIONS = {
  TOP_RIGHT: "top-right",
  TOP_LEFT: "top-left",
  TOP_CENTER: "top-center",
  BOTTOM_RIGHT: "bottom-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_CENTER: "bottom-center",
};

export const TOAST_SEVERITIES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
};

export const TOAST_THEMES = {
  COLORED: "colored", // Vibrant filled color matching severity
  DARK: "dark",       // Dark slate background with luminous severity accents & border
  LIGHT: "light",     // Light background with subtle severity tint
};

export const defaultToastConfig = {
  position: TOAST_POSITIONS.TOP_RIGHT,
  autoClose: 3500,
  hideProgressBar: false,
  pauseOnHover: true,
  closeOnClick: false,
  variant: "filled", // "filled" | "standard" | "outlined"
  theme: TOAST_THEMES.COLORED,
  elevation: 6,
  maxToasts: 5,
  transition: "slide", // "slide" | "grow" | "fade"
  zIndex: 10000050, // Keep above modals, menus, and sticky headers
};

// Global active configuration (can be updated with configureToast)
export let currentToastConfig = { ...defaultToastConfig };

/**
 * Configure global toast defaults
 * @param {Object} customConfig
 */
export const configureToast = (customConfig = {}) => {
  currentToastConfig = {
    ...currentToastConfig,
    ...customConfig,
  };
  return currentToastConfig;
};

/**
 * Convert position string (e.g. 'top-right') to MUI Snackbar anchorOrigin
 */
export const getAnchorOrigin = (position = TOAST_POSITIONS.TOP_RIGHT) => {
  switch (position) {
    case TOAST_POSITIONS.TOP_LEFT:
      return { vertical: "top", horizontal: "left" };
    case TOAST_POSITIONS.TOP_CENTER:
      return { vertical: "top", horizontal: "center" };
    case TOAST_POSITIONS.BOTTOM_RIGHT:
      return { vertical: "bottom", horizontal: "right" };
    case TOAST_POSITIONS.BOTTOM_LEFT:
      return { vertical: "bottom", horizontal: "left" };
    case TOAST_POSITIONS.BOTTOM_CENTER:
      return { vertical: "bottom", horizontal: "center" };
    case TOAST_POSITIONS.TOP_RIGHT:
    default:
      return { vertical: "top", horizontal: "right" };
  }
};

/**
 * Determine slide transition direction based on position
 */
export const getSlideDirection = (position = TOAST_POSITIONS.TOP_RIGHT) => {
  if (position?.includes("right")) return "left";
  if (position?.includes("left")) return "right";
  if (position?.includes("top")) return "down";
  return "up";
};

/**
 * Custom color tokens per severity and theme mode matching Autobet design system
 */
export const getSeverityStyles = (severity = "info", themeMode = "colored") => {
  const isDark = themeMode === "dark";
  const isColored = themeMode === "colored";

  switch (severity) {
    case "success":
      return {
        mainColor: colors?.success?.main || "#10b981",
        lightColor: colors?.success?.light || "#34d399",
        bgColor: isColored
          ? "#059669"
          : isDark
          ? "#0f172a"
          : "#ecfdf5",
        textColor: isDark || isColored ? "#ffffff" : "#065f46",
        borderColor: isDark ? "rgba(16, 185, 129, 0.45)" : isColored ? "rgba(16, 185, 129, 0.6)" : "#10b981",
        glow: "0 10px 25px -3px rgba(16, 185, 129, 0.35)",
        progressColor: isColored ? "rgba(255, 255, 255, 0.7)" : "#10b981",
      };
    case "error":
      return {
        mainColor: colors?.error?.main || "#ef4444",
        lightColor: colors?.error?.light || "#f87171",
        bgColor: isColored
          ? "#dc2626"
          : isDark
          ? "#0f172a"
          : "#fef2f2",
        textColor: isDark || isColored ? "#ffffff" : "#991b1b",
        borderColor: isDark ? "rgba(239, 68, 68, 0.45)" : isColored ? "rgba(239, 68, 68, 0.6)" : "#ef4444",
        glow: "0 10px 25px -3px rgba(239, 68, 68, 0.35)",
        progressColor: isColored ? "rgba(255, 255, 255, 0.7)" : "#ef4444",
      };
    case "warning":
    case "warn":
      return {
        mainColor: colors?.warning?.main || "#f59e0b",
        lightColor: colors?.warning?.light || "#fbbf24",
        bgColor: isColored
          ? "#d97706"
          : isDark
          ? "#0f172a"
          : "#fffbeb",
        textColor: isDark || isColored ? "#ffffff" : "#92400e",
        borderColor: isDark ? "rgba(245, 158, 11, 0.45)" : isColored ? "rgba(245, 158, 11, 0.6)" : "#f59e0b",
        glow: "0 10px 25px -3px rgba(245, 158, 11, 0.35)",
        progressColor: isColored ? "rgba(255, 255, 255, 0.7)" : "#f59e0b",
      };
    case "info":
    default:
      return {
        mainColor: colors?.primary?.main || "#2563eb",
        lightColor: colors?.primary?.light || "#3b82f6",
        bgColor: isColored
          ? "#2563eb"
          : isDark
          ? "#0f172a"
          : "#eff6ff",
        textColor: isDark || isColored ? "#ffffff" : "#1e40af",
        borderColor: isDark ? "rgba(37, 99, 235, 0.45)" : isColored ? "rgba(37, 99, 235, 0.6)" : "#2563eb",
        glow: "0 10px 25px -3px rgba(37, 99, 235, 0.35)",
        progressColor: isColored ? "rgba(255, 255, 255, 0.7)" : "#2563eb",
      };
  }
};
